import React, { useState } from "react";
import { ShieldAlert, KeyRound, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PASSWORD_RULES_HINT } from "./ForgotPasswordDialog";

/**
 * Full-screen blocking gate shown when an admin has reset the user's password
 * (user.must_change_password = true). The user must set their own password
 * before entering the application.
 */
export const ForcePasswordChange = () => {
  const { auth, login, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const code = auth?.municipality?.code;

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/auth/change-password`, {
        current_password: current, new_password: pw1,
      });
      toast.success(r.data.message || "Password updated");
      login({ ...auth, user: { ...auth.user, must_change_password: false } });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="force-password-change-screen">
      <Card className="w-full max-w-sm shadow-md">
        <CardHeader>
          <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center mb-2">
            <ShieldAlert className="h-5 w-5 text-accent-foreground" />
          </div>
          <CardTitle className="font-heading text-lg">Set your own password</CardTitle>
          <CardDescription>
            An administrator reset your password. For your security, please choose a new
            password of your own before continuing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="fc-current">Temporary password</Label>
              <Input id="fc-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                placeholder="Password given by the administrator" required data-testid="force-current-password-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fc-pw1">New password</Label>
              <Input id="fc-pw1" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
                required data-testid="force-new-password-input" />
              <p className="text-[11px] text-muted-foreground">{PASSWORD_RULES_HINT}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="fc-pw2">Confirm new password</Label>
              <Input id="fc-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                required data-testid="force-confirm-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="force-change-submit-button">
              <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save and continue"}
            </Button>
            <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={logout}
              data-testid="force-change-logout-button">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
